import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import Topbar from '../components/Topbar';
import DashboardStats from '../components/DashboardStats';
import DashboardTable from '../components/DashboardTable';
import DashboardModals from '../components/DashboardModals';
import ClientDashboardFilters from '../components/ClientDashboardFilters';
import LoadingWidget from '../../../components/LoadingWidget';
import AlertMessage from '../../../components/AlertMessage';
import Modal from '../../../components/Modal';
import {
  getStatusBadgeColor,
  formatDate,
  formatCurrency,
  getDisplayName,
  getField
} from '../components/DashboardHelpers';
import { useAllClientDashboardData } from '../hooks/ClientDashboardQueries';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ResponsivePie } from '@nivo/pie';

const ClientDashboard = () => {
  const navigate = useNavigate(); // Add navigate hook
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    service_type: '',
    date_from: '',
    date_to: '',
    followup_type: '',
    document_type: '',
    quotation_id: '',
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Alert state
  const [alert, setAlert] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

  const showAlert = useCallback((type, title, message, duration = 2500) => {
    setAlert({ type, title, message, duration });
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Use React Query to fetch all client data with caching
  const {
    stats,
    calculatedStats,
    quotations,
    solarQuotations,
    electricalQuotations,
    electricalServices,
    followups,
    documents,
    isLoading,
    isFetching,
    loadingStates,
    refetchAll,
    refetch
  } = useAllClientDashboardData(API_BASE_URL, queryClient);

  // refetchAll already invalidates ['userProfile'] and re-fetches all queries.
  // We also invalidate ['notifications'] here so the Topbar's mini bell spinner
  // fires consistently (it owns that query independently via its own useQuery).
  const handleRefresh = () => {
    refetchAll();
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    if (modalType === 'document') {
      if (!newItem.quotation) {
        showAlert('error', 'Validation Error', 'Please select a quotation to attach this document to.');
        return;
      }
      if (!newItem.document_type) {
        showAlert('error', 'Validation Error', 'Please select a document type.');
        return;
      }
      if (!newItem._file) {
        showAlert('error', 'Validation Error', 'Please select a file to upload.');
        return;
      }

      try {
        setIsProcessing(true);
        const token = localStorage.getItem('authToken');

        const formData = new FormData();
        formData.append('quotation', newItem.quotation);
        formData.append('document_type', newItem.document_type);
        formData.append('file', newItem._file);
        if (newItem.description) {
          formData.append('description', newItem.description);
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/documents/`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              // Do NOT set Content-Type — axios sets the correct multipart boundary automatically
            }
          }
        );

        if (response.status === 201) {
          const quotationId = newItem.quotation;
          setNewItem({});
          setShowRequestModal(false);
          setModalType('');
          showAlert('success', 'Document Uploaded', `Your document has been attached to quotation #${quotationId} successfully.`, 2500);
          setTimeout(() => refetchAll(), 300);
        }
      } catch (error) {
        if (error.response?.data) {
          const errors = error.response.data;
          const msg = Object.entries(errors)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`)
            .join('\n');
          showAlert('error', 'Upload Failed', msg, 4000);
        } else {
          showAlert('error', 'Upload Failed', 'Failed to upload document. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Default fallback
    setShowRequestModal(false);
    setNewItem({});
    setModalType('');
    if (refetchAll) refetchAll();
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    setShowEditModal(false);
    if (refetchAll) refetchAll();
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) {
      showAlert('error', 'Error', 'No item selected to delete');
      return;
    }

    // Determine the API endpoint and label based on modal type
    let endpoint = '';
    let label = '';

    if (modalType === 'quotation') {
      endpoint = `${API_BASE_URL}/api/quotations/${selectedItem.id}/delete/`;
      label = 'quotation';
    } else if (modalType === 'solar-quotation') {
      endpoint = `${API_BASE_URL}/api/quotations/${selectedItem.quotation}/delete/`;
      label = 'solar quotation';
    } else if (modalType === 'electrical-quotation') {
      endpoint = `${API_BASE_URL}/api/quotations/${selectedItem.quotation}/delete/`;
      label = 'electrical quotation';
    } else if (modalType === 'document') {
      endpoint = `${API_BASE_URL}/api/documents/${selectedItem.id}/`;
      label = 'document';
    } else {
      showAlert('error', 'Error', 'No item selected to delete');
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle both JSON responses and empty 204 responses (DRF default DELETE)
      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }

      if (response.ok) {
        const successMsg = data.message || `${label.charAt(0).toUpperCase() + label.slice(1)} deleted successfully.`;
        showAlert('success', 'Deleted', successMsg);

        // Refresh all data so both the sub-tab and parent quotations tab update
        if (refetchAll) refetchAll();

        // Close modal and reset state
        setShowDeleteModal(false);
        setSelectedItem(null);
        setModalType('');
      } else {
        showAlert('error', 'Delete Failed', data.error || `Failed to delete ${label}`);
      }
    } catch (error) {
      showAlert('error', 'Delete Failed', `Failed to delete ${label}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle token expiry: show warning alert then redirect to /login
  useEffect(() => {
    const handleTokenExpired = () => {
      queryClient.clear();
      setAlert({
        type: 'warning',
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again.',
        duration: 2500,
        onDismiss: () => navigate('/login', { replace: true }),
      });
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    return () => window.removeEventListener('tokenExpired', handleTokenExpired);
  }, [navigate, queryClient]);

  // Proactive JWT expiry check — runs on mount and every 30 seconds
  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        // Decode the payload without verifying the signature (client-side only)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000; // convert to ms

        if (Date.now() >= expiresAt) {
          // Token is already expired — clean up and redirect
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('is_superuser');
          localStorage.removeItem('authTokenStoredAt');
          queryClient.clear();
          window.dispatchEvent(new CustomEvent('tokenExpired'));
        }
      } catch {
        // Malformed token — treat as expired
        window.dispatchEvent(new CustomEvent('tokenExpired'));
      }
    };

    checkTokenExpiry(); // Check immediately on mount
    const interval = setInterval(checkTokenExpiry, 30_000); // Then every 30s
    return () => clearInterval(interval);
  }, [queryClient]);

  // Listen for custom event from Topbar notification
  useEffect(() => {
    const handleViewQuotationFromNotification = async (event) => {
      const { quotationId, notificationId } = event.detail;

      try {
        // Mark notification as read
        const token = localStorage.getItem('authToken');
        if (notificationId) {
          try {
            await axios.post(`${API_BASE_URL}/api/notifications/${notificationId}/mark_read/`, {}, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            // Refetch notifications to update the count
            refetch.notifications();
          } catch (error) {
          }
        }

        // Try to find quotation in cached data first (no API call needed!)
        const existingQuotation = quotations.find(q => q.id === quotationId);

        if (existingQuotation) {
          setSelectedItem(existingQuotation);
          setModalType('quotation');
          setShowViewModal(true);
          setActiveTab('quotations');
        } else {
          // If not in cache, fetch from API and update cache
          const response = await axios.get(`${API_BASE_URL}/api/quotations/${quotationId}/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.data) {
            setSelectedItem(response.data);
            setModalType('quotation');
            setShowViewModal(true);
            setActiveTab('quotations');
            // Refetch to update cache
            refetch.quotations();
          }
        }
      } catch (error) {
        setErrorModal({ show: true, title: 'Quotation Not Found', message: 'Unable to load quotation. It may have been deleted or you may not have permission to view it.' });
      }
    };

    window.addEventListener('viewQuotationFromNotification', handleViewQuotationFromNotification);

    return () => {
      window.removeEventListener('viewQuotationFromNotification', handleViewQuotationFromNotification);
    };
  }, [API_BASE_URL, quotations, refetch, showAlert]);

  // Get filtered data based on active tab
  const getFilteredData = () => {
    let data = [];

    switch (activeTab) {
      case 'quotations':
        data = (quotations || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          // Support various property names for user info
          const userName = item.user_info?.name || item.customer_name || item.customer?.name || '';
          const userEmail = item.user_info?.email || item.customer_email || item.customer?.email || '';

          return (
            userName.toLowerCase().includes(searchLower) ||
            userEmail.toLowerCase().includes(searchLower) ||
            (item.service_type && item.service_type.toLowerCase().includes(searchLower)) ||
            (item.status && item.status.toLowerCase().includes(searchLower)) ||
            (item.notes && item.notes.toLowerCase().includes(searchLower))
          );
        });

        // Apply additional filters
        if (filters.status) {
          data = data.filter(item => item.status === filters.status);
        }
        if (filters.service_type) {
          data = data.filter(item => item.service_type === filters.service_type);
        }
        if (filters.date_from) {
          data = data.filter(item => new Date(item.created_at) >= new Date(filters.date_from));
        }
        if (filters.date_to) {
          data = data.filter(item => new Date(item.created_at) <= new Date(filters.date_to));
        }
        break;

      case 'solar-quotations':
        data = (solarQuotations || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (item.quotation && item.quotation.toString().includes(searchTerm)) ||
            (item.system_type && item.system_type.toLowerCase().includes(searchLower)) ||
            (item.actual_system_size_kw && item.actual_system_size_kw.toString().includes(searchTerm)) ||
            (item.system_size && item.system_size.toString().includes(searchTerm))
          );
        });
        break;

      case 'electrical-quotations':
        data = (electricalQuotations || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (item.quotation && item.quotation.toString().includes(searchTerm)) ||
            (item.property_type && item.property_type.toLowerCase().includes(searchLower)) ||
            (item.property_size && item.property_size.toLowerCase().includes(searchLower)) ||
            (item.service_type && item.service_type.toLowerCase().includes(searchLower))
          );
        });
        break;

      case 'electrical-services':
        data = (electricalServices || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (item.service_type && item.service_type.toLowerCase().includes(searchLower)) ||
            (item.electrical_quotation && item.electrical_quotation.toString().includes(searchTerm)) ||
            (item.description && item.description.toLowerCase().includes(searchLower))
          );
        });
        break;

      case 'followups':
        data = (followups || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (item.notes && item.notes.toLowerCase().includes(searchLower)) ||
            (item.followup_type && item.followup_type.toLowerCase().includes(searchLower)) ||
            (item.quotation && item.quotation.toString().includes(searchTerm)) ||
            (item.status && item.status.toLowerCase().includes(searchLower))
          );
        });

        if (filters.followup_type) {
          data = data.filter(item => item.followup_type === filters.followup_type);
        }
        if (filters.status) {
          data = data.filter(item => item.status === filters.status);
        }
        break;

      case 'documents':
        data = (documents || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (item.document_type && item.document_type.toLowerCase().includes(searchLower)) ||
            (item.title && item.title.toLowerCase().includes(searchLower)) ||
            (item.description && item.description.toLowerCase().includes(searchLower)) ||
            (item.quotation && item.quotation.toString().includes(searchTerm))
          );
        });

        if (filters.document_type) {
          data = data.filter(item => item.document_type === filters.document_type);
        }
        if (filters.quotation_id) {
          data = data.filter(item => item.quotation && item.quotation.toString() === filters.quotation_id);
        }
        break;

      default:
        data = [];
    }

    return data;
  };

  const filteredData = getFilteredData();

  // Calculate pie chart data for service type breakdown
  const pieData = React.useMemo(() => {
    const serviceTypes = ['solar', 'electrical', 'generator'];
    const colorMap = {
      'solar': '#F59E0B',
      'electrical': '#3B82F6',
      'generator': '#8B5CF6',
    };

    const counts = {};
    serviceTypes.forEach(type => { counts[type] = 0; });

    (quotations || []).forEach(item => {
      if (item.service_type && counts.hasOwnProperty(item.service_type)) {
        counts[item.service_type] += 1;
      }
    });

    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);

    if (total === 0) return [];

    return serviceTypes.map(type => ({
      id: type.charAt(0).toUpperCase() + type.slice(1),
      label: type.charAt(0).toUpperCase() + type.slice(1),
      value: counts[type],
      color: colorMap[type],
      percentage: ((counts[type] / total) * 100).toFixed(1)
    })).filter(item => item.value > 0);
  }, [quotations]);

  // Use calculated stats for client-specific data
  const displayStats = calculatedStats || stats;

  return (
    <div className="min-h-screen section bg-gray-50 relative">
      <Topbar />

      {/* Floating Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop with blur effect */}
          <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm"></div>
          {/* Loading widget */}
          <div className="relative transform scale-150">
            <LoadingWidget />
          </div>
        </div>
      )}

      <section className={`max-w-7xl mx-auto pt-20 px-4 sm:px-6 lg:px-8 py-8 ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
        <ClientDashboardFilters
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          onRefresh={handleRefresh}
          isFetching={isFetching}
        />

        {/* Stats Cards */}
        {activeTab === 'overview' && (
          <DashboardStats
            stats={displayStats}
            formatCurrency={formatCurrency}
            isAdmin={false}
          />
        )}

        {/* Main Content Card */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Search and Actions Bar */}
          <div className="border-b border-gray-200 p-6">
            {activeTab !== 'overview' && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="relative flex-1 max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={`Search ${activeTab.replace('-', ' ')}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                  />
                </div>

                {activeTab !== 'solar-quotations' && activeTab !== 'electrical-quotations' && activeTab !== 'followups' && (
                  activeTab === 'documents' ? (
                    <button
                      onClick={() => {
                        setModalType('document');
                        setNewItem({ _quotationsList: quotations || [] });
                        setShowRequestModal(true);
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Document
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        navigate('/quote');
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Request Quote
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          <div className="p-6">
            {activeTab === 'overview' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Quotations */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Recent Quotations
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {loadingStates.quotations ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (quotations || []).length === 0 ? (
                      <div className="text-center py-8">
                        <svg className="h-12 w-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="mt-2 text-gray-500">No quotations yet</p>
                        <button
                          onClick={() => {
                            navigate('/quote');
                          }}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Request Your First Quote
                        </button>
                      </div>
                    ) : (
                      (quotations || []).slice(0, 5).map((item) => (
                        <div
                          key={item.id}
                          className="mb-3 last:mb-0 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedItem(item);
                            setModalType('quotation');
                            setShowViewModal(true);
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {getField(item, 'service_type_display', item.service_type)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatDate(item.created_at)}
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(item.status)}`}>
                              {getField(item, 'status_display', item.status)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Service Type Breakdown Chart */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Service Type Breakdown
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {pieData.length > 0 ? (
                      <div className="h-64">
                        <ResponsivePie
                          data={pieData}
                          margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
                          innerRadius={0.5}
                          padAngle={1}
                          cornerRadius={3}
                          activeOuterRadiusOffset={8}
                          colors={{ datum: 'data.color' }}
                          borderWidth={1}
                          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                          enableArcLinkLabels={true}
                          arcLinkLabelsSkipAngle={10}
                          arcLinkLabelsTextColor="#6B7280"
                          arcLinkLabelsThickness={2}
                          arcLinkLabelsColor={{ from: 'color' }}
                          arcLabelsSkipAngle={10}
                          arcLabelsTextColor="#111827"
                          legends={[
                            {
                              anchor: 'bottom',
                              direction: 'row',
                              justify: false,
                              translateX: 0,
                              translateY: 50,
                              itemsSpacing: 0,
                              itemWidth: 100,
                              itemHeight: 18,
                              itemTextColor: '#6B7280',
                              itemDirection: 'left-to-right',
                              symbolSize: 12,
                              symbolShape: 'circle',
                            }
                          ]}
                          tooltip={({ datum }) => (
                            <div className="bg-white p-2 rounded-lg shadow-lg border border-gray-200">
                              <div className="flex items-center">
                                <div
                                  className="w-3 h-3 rounded-full mr-2"
                                  style={{ backgroundColor: datum.color }}
                                />
                                <strong className="text-gray-900">{datum.label}</strong>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Count: {datum.value}
                              </div>
                              <div className="text-sm text-gray-600">
                                Percentage: {datum.data.percentage}%
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center">
                        <div className="text-center">
                          <svg className="h-12 w-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="mt-2 text-gray-500">No service type data available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <DashboardTable
                activeTab={activeTab}
                filteredData={filteredData}
                setSelectedItem={setSelectedItem}
                setModalType={setModalType}
                setShowViewModal={setShowViewModal}
                setShowEditModal={setShowEditModal}
                getStatusBadgeColor={getStatusBadgeColor}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getField={getField}
                getDisplayName={getDisplayName}
                isClientView={true}
                setShowDeleteModal={setShowDeleteModal}
              />
            )}
          </div>
        </div>
      </section>

      {/* Modals */}
      <DashboardModals
        showViewModal={showViewModal}
        setShowViewModal={setShowViewModal}

        showAddModal={showRequestModal}
        setShowAddModal={setShowRequestModal}

        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}

        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}

        selectedItem={selectedItem}
        modalType={modalType}
        newItem={newItem}
        setNewItem={setNewItem}
        setModalType={setModalType}

        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}

        getDisplayName={getDisplayName}
        formatDate={formatDate}
        formatCurrency={formatCurrency}
        getField={getField}
        getStatusBadgeColor={getStatusBadgeColor}
        customers={[]}

        API_BASE_URL={API_BASE_URL}
        refetchAll={refetchAll}
        isClientView={true}
        showAlert={showAlert}
        isProcessing={isProcessing}
      />

      {/* Alert Message */}
      {alert && (
        <AlertMessage
          type={alert.type}
          title={alert.title}
          message={alert.message}
          duration={alert.duration}
          onDismiss={() => {
            setAlert(prev => {
              if (prev?.onDismiss) prev.onDismiss();
              return null;
            });
          }}
        />
      )}

      {/* Error Modal */}
      {errorModal.show && (
        <Modal
          title={errorModal.title}
          message={errorModal.message}
          onClick={() => setErrorModal({ show: false, title: '', message: '' })}
        />
      )}
    </div>
  );
};

export default ClientDashboard;